import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Sparkles, Pencil, X, ArrowUpRight, HandHeart } from "lucide-react";import { Link } from "react-router-dom";
import { OtterAssistHelpButton } from "@/components/social/OtterAssistHelpButton";
import { OtterAssistResponderModal } from "@/components/social/OtterAssistResponderModal";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Combobox } from "@/components/forms/Combobox";
import { MultiSelect } from "@/components/forms/MultiSelect";
import { GeoRadiusFilter } from "@/components/directory/GeoRadiusFilter";
import { useReferences } from "@/hooks/useReferences";
import { preferredTimezone } from "@/lib/timezones";

// Otter Assist V1 Phase 5 — ONE shared panel for every Chatter composer surface (profile page,
// global desktop, global mobile bottom sheet). It owns eligibility, setup, validation, draft
// persistence and the serialisation into the OPTIONAL `otter_assist` key of the existing Chatter
// creation request (OAD-051). When the feature is unavailable it renders nothing, calls nothing
// and the outgoing Chatter payload is byte-for-byte what it is today.

export const AUDIENCE_OPTIONS = [
  { value: "network", label: "My Network only" },
  { value: "extended", label: "My Network + Extended Network" },
  { value: "community", label: "My Network + Extended + Eligible Community" },
];
export const WORK_OPTIONS = [
  { value: "local", label: "Local" },
  { value: "remote", label: "Remote" },
  { value: "hybrid", label: "Hybrid" },
];
const MODES = [{ value: "none", label: "No preference" },
               { value: "preferred", label: "Preferred" },
               { value: "required", label: "Required" }];
const INDUSTRY_MAX = 3;
const DEFAULT_RADIUS_MI = 25;

/** Every Chatter surface treats these as "nothing was written". */
export const hasPostText = (html) =>
  !!(html || "").replace(/<[^>]*>/g, " ").replace(/&nbsp;/gi, " ").replace(/\s+/g, "").trim();

export const browserTimezone = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; }
  catch (e) { return "UTC"; }
};

/** Offset label for a real IANA id, e.g. "America/Denver — GMT-6". */
const zoneLabel = (zone) => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "shortOffset" })
      .formatToParts(new Date());
    const off = (parts.find((p) => p.type === "timeZoneName") || {}).value;
    return off ? `${zone.replace(/_/g, " ")} — ${off}` : zone.replace(/_/g, " ");
  } catch (e) { return zone.replace(/_/g, " "); }
};

/** Real IANA identifiers only — the backend validates with zoneinfo and never guesses (OAD-050). */
export const timezoneOptions = (fallbackLabels = []) => {
  let zones = [];
  try { zones = (Intl.supportedValuesOf ? Intl.supportedValuesOf("timeZone") : []) || []; }
  catch (e) { zones = []; }
  if (!zones.length) zones = fallbackLabels.filter((z) => z && z.includes("/"));
  const here = preferredTimezone();
  const all = Array.from(new Set([here, ...zones])).filter(Boolean).sort();
  return all.map((z) => ({ value: z, label: zoneLabel(z) }));
};

const fmtLocal = (date, zone) => {
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone: zone, weekday: "short", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit", timeZoneName: "short",
    }).format(date);
  } catch (e) { return date.toISOString(); }
};

/** Client-side mirror of services/otter_assist_urgency.deadline() — preview only. The backend
 *  calculates the authoritative deadline at ACTIVATION time (OAD-049). */
export const deadlinePreview = ({ urgency, sender_timezone, within_days_count }) => {
  const zone = sender_timezone || preferredTimezone();
  const now = new Date();
  const endOfLocalDay = (daysAhead) => {
    try {
      const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit",
      }).formatToParts(new Date(now.getTime() + daysAhead * 86400000));
      const get = (t) => (parts.find((p) => p.type === t) || {}).value;
      const base = new Date(`${get("year")}-${get("month")}-${get("day")}T12:00:00Z`);
      // Walk to 23:59 local by measuring the zone offset at that instant.
      const offsetMin = (() => {
        const d = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "shortOffset" })
          .formatToParts(base).find((p) => p.type === "timeZoneName").value;
        const m = /GMT([+-]\d{1,2})(?::(\d{2}))?/.exec(d);
        return m ? (parseInt(m[1], 10) * 60 + (m[2] ? Math.sign(parseInt(m[1], 10)) * parseInt(m[2], 10) : 0)) : 0;
      })();
      return new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth(), base.getUTCDate(),
        23, 59, 0) - offsetMin * 60000);
    } catch (e) { return new Date(now.getTime() + daysAhead * 86400000); }
  };
  if (urgency === "flexible") return { text: "No firm deadline", at: null };
  let at;
  if (urgency === "immediate") at = endOfLocalDay(0);
  else if (urgency === "asap") at = new Date(now.getTime() + 48 * 3600000);
  else if (urgency === "soon") at = new Date(now.getTime() + 7 * 86400000);
  else at = endOfLocalDay(Math.max(1, Number(within_days_count) || 1));
  return { text: fmtLocal(at, zone), at };
};

export const emptyAssist = () => ({
  interpreted_need: "",
  audience_scope: "community",
  include_followers: false,
  include_fans: false,
  // Every refinement starts at No Preference. Nothing is ever silently populated (OAD-061).
  industry_values: [],
  industry_mode: "none",
  geo: { geoOn: false, center: null, radius: DEFAULT_RADIUS_MI },
  geo_mode: "none",
  work_context: null,
  work_mode: "none",
  urgency: "soon",
  urgency_note: "",
  within_days_count: null,
  sender_timezone: preferredTimezone(),
  // OAD-091 — set only by Relaunch, so the new Assist can record its internal lineage. It never
  // affects allowance, charging, validation or matching.
  relaunched_from_assist_id: null,
});

/** OAD-091 — turn a stored Otter Assist configuration back into composer setup state, so Relaunch
 *  prefills the sender's own need instead of making them recreate it. Nothing is submitted here. */
export const setupFromConfig = (config = {}) => {
  const setup = emptyAssist();
  const filters = config.filters || [];
  const industries = filters.filter((f) => f.kind === "industry");
  const geo = filters.find((f) => f.kind === "geo");
  const work = filters.find((f) => f.kind === "work_setting");
  if (industries.length) {
    setup.industry_values = industries.map((f) => f.value).slice(0, INDUSTRY_MAX);
    setup.industry_mode = industries[0].mode || "preferred";
  }
  if (geo && geo.value) {
    setup.geo = {
      geoOn: true,
      center: {
        lat: geo.value.lat, lng: geo.value.lng, label: geo.value.label,
        granularity: geo.value.granularity, state_code: geo.value.state_code,
        country_code: geo.value.country_code,
      },
      radius: geo.value.radius_mi || DEFAULT_RADIUS_MI,
    };
    setup.geo_mode = geo.mode || "preferred";
  }
  if (work && work.value) {
    setup.work_context = work.value;
    setup.work_mode = work.mode || "preferred";
  }
  return {
    ...setup,
    interpreted_need: config.interpreted_need || "",
    audience_scope: config.audience_scope || "community",
    include_followers: !!config.include_followers,
    include_fans: !!config.include_fans,
    urgency: config.urgency || "soon",
    urgency_note: config.urgency_note || "",
    within_days_count: config.within_days_count || null,
    sender_timezone: config.sender_timezone || preferredTimezone(),
  };
};

const geoValue = (s) => {
  const c = s.geo?.center;
  if (!c) return null;
  return {
    lat: c.lat, lng: c.lng, radius_mi: Number(s.geo.radius) || DEFAULT_RADIUS_MI,
    label: c.label, granularity: c.granularity,
    state_code: c.state_code, country_code: c.country_code,
  };
};

/** The ONLY serialisation point. Inactive refinements are omitted entirely, so nothing the
 *  sender left at No Preference can reach matching (OAD-061). */
export const serializeAssist = (s) => {
  const filters = [];
  if (s.industry_mode !== "none") {
    (s.industry_values || []).forEach((value) =>
      filters.push({ kind: "industry", mode: s.industry_mode, value }));
  }
  const geo = s.geo_mode !== "none" ? geoValue(s) : null;
  if (geo) filters.push({ kind: "geo", mode: s.geo_mode, value: geo });
  if (s.work_mode !== "none" && s.work_context) {
    filters.push({ kind: "work_setting", mode: s.work_mode, value: s.work_context });
  }
  return {
    interpreted_need: (s.interpreted_need || "").trim(),
    audience_scope: s.audience_scope,
    include_followers: !!s.include_followers,
    include_fans: !!s.include_fans,
    filters,
    work_context: s.work_mode !== "none" ? s.work_context || null : null,
    urgency: s.urgency,
    urgency_note: (s.urgency_note || "").trim(),
    within_days_count: s.urgency === "within_days" ? Number(s.within_days_count) || null : null,
    sender_timezone: s.sender_timezone,
    relaunched_from_assist_id: s.relaunched_from_assist_id || null,
  };
};

export const noteLen = (s) => (s || "").trim().length;

export const setupValid = (setup, config) => {
  const min = config?.urgency_note_min || 10;
  const max = config?.urgency_note_max || 280;
  const n = noteLen(setup.urgency_note);
  if (n < min || n > max) return false;
  if ((setup.interpreted_need || "").trim().length < 10) return false;
  if (setup.urgency === "within_days") {
    const c = Number(setup.within_days_count);
    if (!c || c < (config?.within_days_min || 1) || c > (config?.within_days_max || 30)) return false;
  }
  return true;
};

const plainFromHtml = (html) =>
  (html || "").replace(/<[^>]*>/g, " ").replace(/&nbsp;/gi, " ").replace(/\s+/g, " ").trim();

/**
 * Shared Otter Assist composer state. ONE gating request (`GET /otter-assist/config`). When the
 * flags are off that request 404s and this hook stays completely inert: no panel, no
 * composer-state call, no draft row, no validation and no `otter_assist` key (mandatory §19).
 */
export function useOtterAssist({ identityType, identityId, postTypeIds, postDraft, onRestoreDraft }) {
  const [config, setConfig] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [enabled, setEnabled] = useState(false);
  const [setup, setSetup] = useState(emptyAssist);
  const [saved, setSaved] = useState(false);
  const savedSignature = useRef(null);
  const restored = useRef(false);
  const draftRef = useRef(postDraft);
  draftRef.current = postDraft;

  useEffect(() => {
    let active = true;
    setConfig(null);
    setLoadError(false);
    (async () => {
      try {
        const { data } = await api.get(`/otter-assist/config`, {
          params: { identity_type: identityType, identity_id: identityId || undefined },
        });
        if (active) setConfig(data);
      } catch (e) {
        if (!active) return;
        setConfig(null);
        // 404 means the feature is switched off for this identity — stay completely inert
        // (mandatory §19). Anything else is a real failure and must be visible to the member.
        setLoadError(e?.response?.status !== 404);
      }
    })();
    return () => { active = false; };
  }, [identityType, identityId, reloadKey]);

  // Rehydration — only once the feature is confirmed available for this identity.
  useEffect(() => {
    if (!config?.available || restored.current) return undefined;
    restored.current = true;
    let active = true;
    (async () => {
      try {
        const { data } = await api.get(`/otter-assist/composer-state`, {
          params: { identity_type: identityType, identity_id: identityId || undefined },
        });
        if (!active || !data?.assist) return;
        setSetup({ ...emptyAssist(), ...data.assist });
        setEnabled(true);
        setSaved(true);
        savedSignature.current = JSON.stringify({ post: data.post || null, assist: data.assist });
        if (data.post && onRestoreDraft) onRestoreDraft(data.post);
        toast.success("Otter Assist draft restored");
      } catch (e) { /* no draft is the normal case */ }
    })();
    return () => { active = false; };
  }, [config, identityType, identityId, onRestoreDraft]);

  const eligibleIds = useMemo(
    () => new Set((config?.eligible_post_types || []).map((t) => t.id)), [config]);
  const postTypeEligible = (postTypeIds || []).some((id) => eligibleIds.has(id));
  // OAD-061 — Otter Assist needs something to match on: a real post AND an eligible label.
  const hasContent = hasPostText(postDraft?.body);
  const composable = postTypeEligible && hasContent;

  // Debounced autosave. Never on a keystroke, never when Otter Assist is not selected, and never
  // when the serialised state has not materially changed (OAD-008).
  useEffect(() => {
    if (!config?.available || !enabled || !composable) return undefined;
    const timer = setTimeout(async () => {
      const body = {
        identity_type: identityType, identity_id: identityId || null,
        post: draftRef.current || {}, assist: setup,
      };
      const signature = JSON.stringify({ post: body.post, assist: body.assist });
      if (signature === savedSignature.current) return;
      try {
        await api.put(`/otter-assist/composer-state`, body);
        savedSignature.current = signature;
      } catch (e) { /* draft persistence never blocks posting */ }
    }, 1500);
    return () => clearTimeout(timer);
  }, [config, enabled, composable, setup, postDraft, identityType, identityId]);

  const discardState = useCallback(async () => {
    if (savedSignature.current === null) return;
    savedSignature.current = null;
    try {
      await api.delete(`/otter-assist/composer-state`, {
        params: { identity_type: identityType, identity_id: identityId || undefined },
      });
    } catch (e) { /* ignore */ }
  }, [identityType, identityId]);

  const reset = useCallback((options = {}) => {
    setEnabled(false); setSaved(false); setSetup(emptyAssist());
    if (options.keepDraft !== true) discardState();
  }, [discardState]);

  const payload = enabled && saved && composable && config?.available
    ? serializeAssist(setup) : undefined;

  return {
    config, available: !!config?.available, reason: config?.reason, postTypeEligible,
    loadError, retryConfig: () => setReloadKey((n) => n + 1),
    hasContent, composable,
    identityType, identityId, enabled, setEnabled, setup, setSetup, saved, setSaved,
    reset, discardState, payload,
  };
}

const DISABLED_COPY = (config) => ({
  upgrade_required: "Otter Assist is included with PRO and CAPITAL plans.",
  allowance_exhausted: `You've used every Otter Assist in this cycle${config?.cycle_end ? ` — your allowance resets on ${new Date(config.cycle_end).toLocaleDateString()}.` : "."}`,
  open_limit_reached: `You already have ${config?.open_limit || 5} active Otter Assists. Close one to start another.`,
  identity_not_permitted: "You can't use Otter Assist for this profile.",
  account_ineligible: "Otter Assist isn't available for this account.",
  feature_unavailable: "Otter Assist isn't available right now.",
});

/** Every setup control. Shared by the composer panel and the edit-and-rerun dialog. */
export function OtterAssistSetupFields({ config, setup, setSetup, identityType, industryOpts = [],
                                         idSuffix = "" }) {
  const { references } = useReferences();
  const tzOptions = useMemo(
    () => timezoneOptions((references.timezones || []).map((t) => t.label)), [references.timezones]);
  const industries = industryOpts.length
    ? industryOpts
    : (references.industries || []).map((i) => ({ value: i.value, label: i.label }));
  const set = (patch) => setSetup((s) => ({ ...s, ...patch }));
  const withinNeeded = setup.urgency === "within_days";
  const preview = deadlinePreview(setup);
  const noteMax = config?.urgency_note_max || 280;
  const noteMin = config?.urgency_note_min || 10;
  const orgIdentity = identityType && identityType !== "personal";

  const ModeToggle = ({ value, onChange, testId }) => (
    <div className="flex gap-1" data-testid={testId}>
      {MODES.map((m) => (
        <Button
          key={m.value} type="button" size="sm" variant={value === m.value ? "default" : "outline"}
          className="h-7 px-2.5 text-[11px]" onClick={() => onChange(m.value)}
          data-testid={`${testId}-${m.value}`}
        >{m.label}</Button>
      ))}
    </div>
  );

  return (
    <div className="space-y-4" data-testid={`otter-assist-setup${idSuffix}`}>
      <div>
        <Label className="text-xs font-[560]">What you need</Label>
        <Textarea
          value={setup.interpreted_need} rows={3} className="mt-1.5"
          placeholder="Otter Assist's read of your request — edit anything that isn't right."
          onChange={(e) => set({ interpreted_need: e.target.value.slice(0, 600) })}
          data-testid={`otter-assist-need${idSuffix}`}
        />
        <p className="mt-1 text-[11px] text-muted-foreground">
          Otter Assist interprets your request when matching runs. This summary is what it starts from.
        </p>
      </div>

      <div>
        <Label className="text-xs font-[560]">Who it can reach</Label>
        <RadioGroup
          value={setup.audience_scope} onValueChange={(v) => set({ audience_scope: v })}
          className="mt-1.5 space-y-1.5"
        >
          {AUDIENCE_OPTIONS.map((o) => (
            <div key={o.value} className="flex items-center gap-2">
              <RadioGroupItem value={o.value} id={`oa-scope-${o.value}${idSuffix}`}
                data-testid={`otter-assist-scope-${o.value}${idSuffix}`} />
              <Label htmlFor={`oa-scope-${o.value}${idSuffix}`} className="text-xs font-normal">{o.label}</Label>
            </div>
          ))}
        </RadioGroup>
        {orgIdentity && (
          <div className="mt-2 flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-xs">
              <Checkbox checked={setup.include_followers}
                onCheckedChange={(v) => set({ include_followers: !!v })}
                data-testid={`otter-assist-followers${idSuffix}`} /> Include followers
            </label>
            <label className="flex items-center gap-2 text-xs">
              <Checkbox checked={setup.include_fans}
                onCheckedChange={(v) => set({ include_fans: !!v })}
                data-testid={`otter-assist-fans${idSuffix}`} /> Include fans
            </label>
          </div>
        )}
      </div>

      <div className="rounded-md border border-border bg-card/60 p-3">
        <div className="text-xs font-[600] uppercase tracking-wide text-muted-foreground">Refine matches</div>
        <p className="mt-1 text-[11px] text-muted-foreground">
          All optional. Leave anything on No preference and Otter Assist decides for itself.
        </p>
        <div className="mt-3 space-y-3">
          <div>
            <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
              <Label className="text-xs font-[560]">Industry <span className="font-normal text-muted-foreground">(up to {INDUSTRY_MAX})</span></Label>
              <ModeToggle value={setup.industry_mode} onChange={(v) => set({ industry_mode: v })}
                testId={`otter-assist-industry-mode${idSuffix}`} />
            </div>
            <MultiSelect options={industries} value={setup.industry_values} max={INDUSTRY_MAX}
              onChange={(v) => set({ industry_values: v })} placeholder="Any industry"
              testId={`otter-assist-industry${idSuffix}`} />
          </div>

          <div>
            <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
              <Label className="text-xs font-[560]">Location</Label>
              <ModeToggle value={setup.geo_mode} onChange={(v) => set({ geo_mode: v })}
                testId={`otter-assist-geo-mode${idSuffix}`} />
            </div>
            <div data-testid={`otter-assist-geo${idSuffix}`}>
              <GeoRadiusFilter
                value={setup.geo || { geoOn: false, center: null, radius: DEFAULT_RADIUS_MI }}
                onChange={(patch) => set({ geo: { ...(setup.geo || {}), ...patch } })}
                testId={`otter-assist-geo-radius${idSuffix}`}
              />
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Required means a member must have a public location inside that area. Preferred only
              ranks them higher. A location is separate from work setting.
            </p>
          </div>

          <div>
            <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
              <Label className="text-xs font-[560]">Work setting</Label>
              <ModeToggle value={setup.work_mode} onChange={(v) => set({ work_mode: v })}
                testId={`otter-assist-work-mode${idSuffix}`} />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm"
                variant={!setup.work_context ? "default" : "outline"} className="h-8"
                onClick={() => set({ work_context: null, work_mode: "none" })}
                data-testid={`otter-assist-work-not_applicable${idSuffix}`}>Not applicable</Button>
              {WORK_OPTIONS.map((o) => (
                <Button key={o.value} type="button" size="sm"
                  variant={setup.work_context === o.value ? "default" : "outline"}
                  className="h-8" onClick={() => set({ work_context: o.value })}
                  data-testid={`otter-assist-work-${o.value}${idSuffix}`}>{o.label}</Button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div>
        <Label className="text-xs font-[560]">How soon do you need help?</Label>
        <div className="mt-1.5 flex flex-wrap gap-2">
          {(config?.urgency_options || []).map((o) => (
            <Button key={o.value} type="button" size="sm"
              variant={setup.urgency === o.value ? "default" : "outline"} className="h-8"
              onClick={() => set({
                urgency: o.value,
                within_days_count: o.requires_days ? (setup.within_days_count || 7) : null,
              })}
              data-testid={`otter-assist-urgency-${o.value}${idSuffix}`}>{o.label}</Button>
          ))}
        </div>
        {withinNeeded && (
          <div className="mt-2 flex items-center gap-2">
            <Input type="number" min={config?.within_days_min || 1} max={config?.within_days_max || 30}
              value={setup.within_days_count || ""} className="h-9 w-24"
              onChange={(e) => set({ within_days_count: Number(e.target.value) })}
              data-testid={`otter-assist-within-days${idSuffix}`} />
            <span className="text-xs text-muted-foreground">
              days ({config?.within_days_min || 1}–{config?.within_days_max || 30})
            </span>
          </div>
        )}
        <Textarea
          value={setup.urgency_note} rows={2} className="mt-2"
          placeholder="Why this timing matters (required)"
          onChange={(e) => set({ urgency_note: e.target.value.slice(0, noteMax) })}
          data-testid={`otter-assist-urgency-note${idSuffix}`}
        />
        <div className="mt-1 flex items-center justify-between text-[11px] text-muted-foreground">
          <span>Required — {noteMin}–{noteMax} characters</span>
          <span data-testid={`otter-assist-note-count${idSuffix}`}>{noteLen(setup.urgency_note)}/{noteMax}</span>
        </div>
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        <div>
          <Label className="text-xs font-[560]">Your timezone</Label>
          <div className="mt-1.5">
            <Combobox options={tzOptions} value={setup.sender_timezone}
              onChange={(v) => set({ sender_timezone: v })} placeholder="Select a timezone"
              searchPlaceholder="Search timezones…" emptyText="No timezone found."
              testId={`otter-assist-timezone${idSuffix}`} />
          </div>
          <p className="mt-1 text-[11px] text-muted-foreground">Prefilled from your account settings.</p>
        </div>
        <div className="rounded-md bg-secondary/60 p-2.5">
          <div className="text-[11px] font-[600] uppercase tracking-wide text-muted-foreground">Deadline preview</div>
          <div className="mt-0.5 text-xs font-[560] text-foreground"
            data-testid={`otter-assist-deadline-preview${idSuffix}`}>{preview.text}</div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            Confirmed when Otter Assist activates.
          </div>
        </div>
      </div>
    </div>
  );
}

export function OtterAssistComposerPanel({ assist, body, industryOpts = [], idSuffix = "" }) {
  const { config, available, reason, postTypeEligible, hasContent, composable,
    identityType, enabled, setEnabled, loadError, retryConfig,
    setup, setSetup, saved, setSaved, reset } = assist;
  // PREVIEW-ONLY QA scaffold: `config.qa` is present only for an admin while the preview flag is on.
  const [qaScenario, setQaScenario] = useState("off");
  useEffect(() => { if (config?.qa?.scenario) setQaScenario(config.qa.scenario); },
    [config?.qa?.scenario]);

  // A real failure (network / 5xx) is shown to the member; a 404 means the feature is off for
  // this identity and must stay completely silent.
  if (!config && loadError) {
    return (
      <div className="rounded-lg border border-amber-300 bg-amber-50/60 p-3"
        data-testid={`otter-assist-panel${idSuffix}`} role="alert" aria-live="polite">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <Sparkles className="h-4 w-4 shrink-0 text-amber-700" aria-hidden="true" />
            <span className="text-xs font-[700] uppercase tracking-wider text-amber-800">
              Otter Assist
            </span>
          </div>
          <Button type="button" size="sm" variant="outline" onClick={retryConfig}
            data-testid={`otter-assist-panel-retry${idSuffix}`}>
            Try again
          </Button>
        </div>
        <p className="mt-2 text-[11px] text-amber-900">
          Otter Assist couldn't load just now. Your post is unaffected — you can publish it as
          normal, or try again.
        </p>
      </div>
    );
  }
  if (!config) return null;   // flags off / unavailable — nothing renders, nothing is called

  const disabledCopy = DISABLED_COPY(config)[reason];
  const canSave = setupValid(setup, config);

  const turnOn = () => {
    setSetup((s) => ({
      ...s,
      interpreted_need: s.interpreted_need || plainFromHtml(body).slice(0, 500),
      sender_timezone: s.sender_timezone || preferredTimezone(),
    }));
    setEnabled(true);
  };

  return (
    <div className="rounded-lg border border-teal-700/30 bg-teal-50/50 p-3" data-testid={`otter-assist-panel${idSuffix}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <Sparkles className="h-4 w-4 shrink-0 text-teal-700" />
          <span className="text-xs font-[700] uppercase tracking-wider text-teal-800">Otter Assist</span>
          {available && (
            <span className="text-[11px] text-muted-foreground" data-testid={`otter-assist-allowance${idSuffix}`}>
              {config.allowance_remaining} of {config.allowance_total} left · {config.open_count}/{config.open_limit} active
            </span>
          )}
        </div>
        {reason === "upgrade_required" ? (
          <Button asChild type="button" size="sm" variant="outline" data-testid={`otter-assist-upgrade${idSuffix}`}>
            <Link to="/dashboard/billing">Upgrade to use it <ArrowUpRight className="ml-1 h-3.5 w-3.5" /></Link>
          </Button>
        ) : (
          <Button
            type="button" size="sm" variant={enabled ? "secondary" : "default"}
            disabled={!available || !composable}
            onClick={() => (enabled ? reset() : turnOn())}
            data-testid={`otter-assist-toggle${idSuffix}`}
          >
            {enabled ? (<><X className="mr-1 h-3.5 w-3.5" /> Remove Otter Assist</>) : "Add Otter Assist"}
          </Button>
        )}
      </div>

      {config.qa && (
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-dashed border-amber-500/60 bg-amber-50/70 px-2.5 py-2"
          data-testid={`otter-assist-qa-scenario-row${idSuffix}`}>
          <span className="text-[11px] font-[700] uppercase tracking-wider text-amber-800">
            Preview QA — seeded matches
          </span>
          <select
            className="h-7 rounded-md border border-input bg-card px-2 text-[11px]"
            value={qaScenario}
            data-testid={`otter-assist-qa-scenario${idSuffix}`}
            onChange={async (e) => {
              const scenario = e.target.value;
              setQaScenario(scenario);
              try {
                await api.put("/otter-assist/qa/scenario", { scenario });
                toast.success("QA scenario set — it applies to your next Otter Assist run");
              } catch (err) { toast.error("Could not set the QA scenario"); }
            }}
          >
            {(config.qa.options || []).map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <span className="text-[11px] text-amber-800">Admin-only · Preview-only · no delivery</span>
        </div>
      )}

      {!hasContent && (
        <p className="mt-2 text-[11px] text-muted-foreground" data-testid={`otter-assist-content-hint${idSuffix}`}>
          Write your post first to use Otter Assist.
        </p>
      )}
      {hasContent && !postTypeEligible && (
        <p className="mt-2 text-[11px] text-muted-foreground" data-testid={`otter-assist-post-type-hint${idSuffix}`}>
          Select Request, Question or Work to use Otter Assist.
        </p>
      )}
      {composable && !available && disabledCopy && (
        <p className="mt-2 text-[11px] font-[540] text-amber-800" data-testid={`otter-assist-disabled-reason${idSuffix}`}>
          {disabledCopy}
        </p>
      )}

      {enabled && available && composable && (
        saved ? (
          <div className="mt-3 rounded-md border border-teal-700/20 bg-card p-3" data-testid={`otter-assist-summary${idSuffix}`}>
            <div className="text-xs font-[560] text-foreground">Otter Assist is ready</div>
            <p className="mt-1 line-clamp-2 text-[11px] text-muted-foreground">{setup.interpreted_need}</p>
            <p className="mt-1 text-[11px] text-muted-foreground">
              {(config.urgency_options || []).find((o) => o.value === setup.urgency)?.label} ·{" "}
              {deadlinePreview(setup).text}
            </p>
            <div className="mt-2 flex gap-2">
              <Button type="button" size="sm" variant="outline" className="h-7 text-[11px]"
                onClick={() => setSaved(false)} data-testid={`otter-assist-edit${idSuffix}`}>
                <Pencil className="mr-1 h-3 w-3" /> Edit
              </Button>
              <Button type="button" size="sm" variant="ghost" className="h-7 text-[11px]"
                onClick={() => reset()} data-testid={`otter-assist-remove${idSuffix}`}>
                <X className="mr-1 h-3 w-3" /> Remove
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-3">
            <OtterAssistSetupFields config={config} setup={setup} setSetup={setSetup}
              identityType={identityType} industryOpts={industryOpts} idSuffix={idSuffix} />
            <p className="mt-3 text-[11px] text-muted-foreground" data-testid={`otter-assist-charge-disclosure${idSuffix}`}>
              Posting with Otter Assist uses one Assist automatically if five or more matches
              are found. With fewer than five, nothing is used until you choose to continue.
            </p>
            <div className="mt-2 flex items-center gap-2">
              <Button type="button" size="sm" disabled={!canSave}
                onClick={() => { setSaved(true); toast.success("Otter Assist ready"); }}
                data-testid={`otter-assist-save${idSuffix}`}>Save Otter Assist</Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => reset()}
                data-testid={`otter-assist-cancel${idSuffix}`}>Cancel</Button>
            </div>
          </div>
        )
      )}
    </div>
  );
}

// --- public post presentation (Phase 5: presentation and gating only) --------------------
// OAD-062 / OAD-063 — the backend sends a SANITISED `assist_public_status`. Internal matching
// states (configured / matching / awaiting_confirmation / no_matches / matching_failed /
// admin_paused) never arrive here at all: an open need is simply "Active" whatever the matching
// engine is doing or found. The client never maps internal states, and never guesses. The whole
// presentation disappears only when the sender detaches the Assist ("Continue without Otter
// Assist"), which removes `assist_id` from the post.
const PUBLIC_STATUS_COPY = {
  active: "Active", pending: "Pending", help_received: "Help received",
  closed: "Closed", expired: "Expired",
};
const URGENCY_SHORT = {
  immediate: "Immediate — today", asap: "ASAP — within 48 hours", soon: "Soon — within 7 days",
  within_days: "Within a set number of days", flexible: "Flexible — no firm deadline",
};

export function OtterAssistPostPill({ post }) {
  const [showResponders, setShowResponders] = React.useState(false);
  const status = post?.assist_public_status;
  if (!post?.assist_id || !PUBLIC_STATUS_COPY[status]) return null;
  const deadline = post.assist_deadline_at
    ? fmtLocal(new Date(post.assist_deadline_at), browserTimezone())
    : "No firm deadline";
  const offered = post.assist_offered_count || 0;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-md border border-yellow-300/70 bg-yellow-50 px-2.5 py-2"
      data-testid={`otter-assist-post-pill-${post.id}`}>
      <span className="inline-flex items-center gap-1 text-[11px] font-[700] uppercase tracking-wider text-teal-800">
        <Sparkles className="h-3.5 w-3.5" /> Otter Assist
      </span>
      {post.assist_urgency && (
        <span className="text-[11px] text-foreground" data-testid={`otter-assist-urgency-${post.id}`}>
          {URGENCY_SHORT[post.assist_urgency] || post.assist_urgency}
        </span>
      )}
      <span className="text-[11px] text-muted-foreground" data-testid={`otter-assist-deadline-${post.id}`}>
        {post.assist_deadline_at ? `By ${deadline}` : deadline}
      </span>
      <span className="rounded-full bg-card px-2 py-0.5 text-[11px] font-[560] text-foreground"
        data-testid={`otter-assist-status-${post.id}`}>{PUBLIC_STATUS_COPY[status]}</span>
      {/* OAD-079/OAD-080 — the sender's own post shows ONLY how many people intentionally offered
          to help, and opens the shared responder view. Never members reached or wave counts. */}
      {offered > 0 && (
        <>
          <Button type="button" size="sm" variant="ghost"
            className="ml-auto h-7 px-2 text-[11px] font-[640] text-teal-800 hover:bg-teal-100"
            onClick={() => setShowResponders(true)}
            data-testid={`otter-assist-offered-count-${post.id}`}>
            <HandHeart className="mr-1 h-3.5 w-3.5" />
            {offered === 1 ? "1 offered to help" : `${offered} offered to help`}
          </Button>
          <OtterAssistResponderModal assistId={post.assist_id} open={showResponders}
            onOpenChange={setShowResponders} />
        </>
      )}
      {/* Rendered ONLY when the backend says help can actually be offered right now — never as a
          greyed-out control. Eligibility never depends on the AI match count (OAD-074). */}
      {(post.assist_can_help || post.assist_my_response) && (
        <OtterAssistHelpButton assistId={post.assist_id} testIdSuffix={post.id}
          className={offered > 0 ? "" : "ml-auto"}
          initialState={{ can_help: !!post.assist_can_help,
                          my_response: post.assist_my_response === "offered" ? "offered" : null }} />
      )}
    </div>
  );
}

export default OtterAssistComposerPanel;
